import type { FilterOption } from '@lighthouse/core'
import { type FieldValue, SelectedMode } from '@lighthouse/core'
import { getCascadeOption, getMainTableRecordId, getWithScopeId } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import { atom } from 'jotai'

import * as srv from '@/services'

import { currentAppIdAtom, currentEnvIdAtom } from '../application/state'
import { addRecordAtom, addRecordsAtom, deleteRecordAtom, setRecordAtom } from '../dataSource/action'
import { dataSourceAtomFamily, recordPoolAtom, viewIndependentDataAtomFamily } from '../dataSource/state'
import type { CreateViewRecordInDataDrawerAtomPayload } from '../dataSource/types'
import { blockUpdateListAtom } from '../page/state'
import { filterBlockOptionsAtom } from './state'
import type {
    FetchChartBlockPayload,
    FetchViewRecordsAtomPayload,
    HandleFetchFilterCascadeOptionsPayload,
    HandleFetchFilterOptionsPayload
} from './types'

/** 获取视图数据 */
export const fetchViewRecordsAtom = atom(null, async (get, set, payload: FetchViewRecordsAtomPayload) => {
    const appId = get(currentAppIdAtom)
    const envId = get(currentEnvIdAtom)
    set(blockUpdateListAtom, [])
    const { stackId, viewId, uniqueId, scope, isSyncComponent, pagination, search, params, disableFetchCount, abort } = payload
    const viewData = await srv.getViewByBlock({ appId, envId, viewId, isSyncComponent, pagination, params, search, abort })
    if (!viewData) {
        return
    }
    const { records, datasource } = viewData

    set(addRecordsAtom, { records })
    const recordPoints = records.map(record => ({ id: record.id }))

    const rowTotal = disableFetchCount ? recordPoints.length : await srv.getViewCount(payload)
    set(viewIndependentDataAtomFamily({ stackId, dsId: datasource.id, viewId: getWithScopeId(uniqueId, scope) }), draft => {
        draft.recordPoints = recordPoints
        draft.currentPage = pagination.currentPage ?? 1
        draft.search = search
        draft.rowTotal = rowTotal
    })
    return viewData
})

/** 加载视图数据 */
export const loadViewRecordsAtom = atom(null, async (get, set, payload: FetchViewRecordsAtomPayload) => {
    const appId = get(currentAppIdAtom)
    const envId = get(currentEnvIdAtom)
    const { stackId, scope, viewId, uniqueId, isSyncComponent, pagination, search, params, abort } = payload
    const viewData = await srv.getViewByBlock({ appId, envId, viewId, isSyncComponent, pagination, params, search, abort })
    if (!viewData) {
        return []
    }
    const { records, datasource } = viewData
    set(addRecordsAtom, { records })
    const recordPoints = records.map(record => ({ id: record.id }))

    set(viewIndependentDataAtomFamily({ stackId, dsId: datasource.id, viewId: getWithScopeId(uniqueId, scope) }), draft => {
        const existingIds = new Set(draft.recordPoints.map(p => p.id))
        const newPoints = recordPoints.filter(p => !existingIds.has(p.id))
        draft.recordPoints = [...draft.recordPoints, ...newPoints]
        draft.currentPage = pagination.currentPage ?? 1
        draft.search = search
    })
    return records
})

/** 获取评论组件子级回复数量 */
export const loadCommentViewReplayCountAtom = atom(null, (get, set, payload: FetchViewRecordsAtomPayload) => {
    return srv.getViewCount(payload)
})

/** 加载评论组件子级数据 */
export const loadCommentViewRecordsAtom = atom(null, async (get, set, payload: FetchViewRecordsAtomPayload) => {
    const appId = get(currentAppIdAtom)
    const envId = get(currentEnvIdAtom)
    const { stackId, scope, viewId, uniqueId, isSyncComponent, pagination, search, params, pageId, abort } = payload
    const viewData = await srv.getViewByBlock({ appId, envId, viewId, isSyncComponent, pagination, params, search, abort })
    if (!viewData) {
        return []
    }
    const { records, datasource } = viewData
    set(addRecordsAtom, { records })
    const recordPoints = records.map(record => ({ id: record.id }))
    set(viewIndependentDataAtomFamily({ stackId, dsId: datasource.id, viewId: getWithScopeId(uniqueId, scope) }), draft => {
        const existingIds = new Set(draft.recordPoints.map(p => p.id))
        const newPoints = recordPoints.filter(p => !existingIds.has(p.id))
        draft.recordPoints = [...draft.recordPoints, ...newPoints]
    })
    return records
})

/** 获取图表数据 */
export const fetchChartBlockAtom = atom(null, async (_, set, payload: FetchChartBlockPayload) => {
    set(blockUpdateListAtom, [])
    const data = await srv.getDsGraphView(payload)
    return data.chart
})

// 获取文本 成员筛选器 options
export const handleFetchFilterOptionsAtom = atom(null, async (_, set, payload: HandleFetchFilterOptionsPayload) => {
    const { dsId, blockId, filterItemId, showFieldId, fieldId } = payload
    const data = await srv.getRelativeOptions(payload)
    const options = data.map(
        item =>
            ({
                label: item.content[showFieldId]?.value,
                value: item.content[fieldId]?.value
            } as FilterOption)
    )
    set(filterBlockOptionsAtom, draft => {
        draft[`${blockId}-${filterItemId}`] = options
    })
    return data
})

// 获取级联筛选器 options
export const handleFetchFilterCascadeOptionsAtom = atom(null, async (get, set, payload: HandleFetchFilterCascadeOptionsPayload) => {
    const appId = get(currentAppIdAtom)
    const envId = get(currentEnvIdAtom)
    const {
        dsId,
        sortFieldPointer = '',
        fieldPointer,
        filter,
        parentFieldPointer,
        blockId,
        filterItemId,
        pageSize,
        showFieldPointer
    } = payload
    const res = await srv.getDs({
        appId,
        envId,
        dsId,
        filter,
        pagination: { currentPage: 1, pageSize },
        sorts: [{ id: 'cascadeSortId', order: 'ASC', fieldId: sortFieldPointer }]
    })
    // const a = res?.records?.filter(record => record?.content?.['ID']?.value === '002')
    const usedRecords = res?.records?.filter(record => record?.content?.[fieldPointer]?.value)

    const options =
        usedRecords
            ?.filter(record => !record?.content?.[parentFieldPointer]?.value)
            .map(record => getCascadeOption({ parentFieldPointer, fieldPointer, showFieldPointer, record, records: usedRecords }))
            .filter(item => item.value) ?? []
    set(filterBlockOptionsAtom, draft => {
        draft[`${blockId}-${filterItemId}`] = options
    })
    return options
})

/** 数据查看器创建视图数据 */
export const createViewRecordInDataDrawerAtom = atom(null, async (get, set, payload: CreateViewRecordInDataDrawerAtomPayload) => {
    const { stackId, viewId, scope, recordId, appId, envId, dsId, content, beforeId } = payload
    const dataSourceAtom = dataSourceAtomFamily({ appId, envId, dsId })
    const newRecordId = recordId || nanoid(32)
    const dataSource = get(dataSourceAtom)
    if (!dataSource) {
        return
    }
    const { schema } = dataSource
    // createRecordAction(newRecordId, appId, dsId, beforeId, set)

    // const record = await srv.createRecord(appId, newRecordId, dsId, beforeId)
    const rContent: Record<string, FieldValue> = Object.fromEntries(
        Object.entries(schema).map(([fieldId, config]) => {
            return [
                fieldId,
                {
                    value: ''
                }
            ]
        })
    )
    const newRecord = { content: rContent, appId, envId, dsId, id: newRecordId }
    set(addRecordAtom, newRecord)
    set(setRecordAtom, {
        envId,
        recordId: newRecordId,
        dsId,
        record: newRecord
    })
    set(viewIndependentDataAtomFamily({ stackId, dsId, viewId: getWithScopeId(viewId, scope) }), draft => {
        draft.recordPoints.unshift({ id: newRecordId })
        if (draft.rowTotal) {
            draft.rowTotal += 1
        } else {
            draft.rowTotal = 1
        }
    })
    const record = await srv.createRecord({ appId, envId, id: newRecordId, dsId, content, beforeId })
    if (!record) {
        set(deleteRecordAtom, {
            envId,
            dsId,
            mode: SelectedMode.CURRENT_PAGE,
            recordIds: [newRecordId]
        })
        set(viewIndependentDataAtomFamily({ stackId, dsId, viewId: getWithScopeId(viewId, scope) }), draft => {
            draft.recordPoints.unshift({ id: newRecordId })
            if (draft.rowTotal || draft.rowTotal === 0) {
                draft.rowTotal -= 1
            }
        })
        return
    }
    set(recordPoolAtom, draft => {
        // 更新所有 mainTableRecordId 相同的记录（处理连接表）
        for (const rec of draft.values()) {
            if (rec.appId === appId && rec.envId === envId && rec.dsId === dsId && getMainTableRecordId(rec.id) === record.id) {
                Object.keys(record.content).forEach(k => {
                    rec.content[k] = record.content[k]
                })
            }
        }
    })
    return newRecordId
})
